import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { BadgeCheck, GraduationCap } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuthStore } from '@/stores/auth';
import { flattenNodes } from '@/lib/node-tree';
import type { NodesResponse } from '@/stores/scope';

/**
 * `/talent/coverage` (人・組織機能 増分⑤ SC-06、★月次会議の主画面)。
 * 設計: docs/design/v04-people-org-requirements-detail.md §4-1 / §6 (D-636/D-647/D-650)。
 *
 * ⚠️ D-650: 処遇金額は既定で表示しない (このAPIはそもそも金額を返さないため、実装上は自動的に満たす)。
 * ⚠️ 修正2/§4.2: 充足／未充足のバッジ・店舗名の順位や色による優劣表現は出さない。
 *   面ごとの確定人数・未確認人数をそのまま示すだけに留める。
 *
 * `GET /competency/coverage` は node_id を1件しか受け付けない (面の集合はテナント全体で共通、
 * 店舗ごとに変わるのは在籍者側だけ、D-687)。複数店舗のグリッドは店舗ごとに個別リクエストして
 * クライアント側で束ねる (TalentCertificationsPage.tsx の useQueries と同じパターン)。
 *
 * ⚠️ D-636と同じ理由 (isResourceInPermissionScope はself scopeの分岐を持たない) で、
 * self scope しか持たないユーザーは navigation.ts の requiresNonSelfScope で画面自体を出さない
 * (CompetencyMatrixPage.tsx と同じ判断)。own_store/node_scope ユーザーは user.stores (JWTの
 * scope_stores) に含まれるノードだけを選択肢にすることで、選んだ瞬間403になるデッドエンドを避ける。
 */

type FaceCoverageRow = {
  face_id: string; business_function_id: string | null; abstraction_level: string | null;
  required_scale_level_id: string | null; confirmed_count: number; unconfirmed_count: number;
  undeterminable: null | 'scale_not_assigned' | 'face_disabled';
};
/** v0.5 ② (D-787/§3-1-6): 親ノード要求 (「くくり」経由の必要スキル) の充足状況。
 *  給与算入が既定 OFF のテナントでは、この節が唯一の確認手段になる。 */
type CoverageGroupRow = {
  role_profile_id: string; target_entity_type: 'business_function' | 'skill'; target_id: string;
  aggregation: 'all_children' | 'required_only'; satisfied_count: number; subject_count: number;
  expanded_face_count: number; undeterminable: null | 'scale_not_assigned' | 'face_disabled';
  fully_covered_by_explicit: boolean;
};
type CoverageResponse = { node_id: string; faces: FaceCoverageRow[]; groups: CoverageGroupRow[] };
type RoleProfileMaster = { role_profile_id: string; label: string };
const AGGREGATION_LABELS: Record<'all_children' | 'required_only', string> = {
  all_children: '直系の子をすべて満たす', required_only: '必須指定の子のみを満たす',
};

type MatrixSkill = { skill_id: string; label: string; business_function_id: string | null; abstraction_level: string | null };
type BusinessFunction = { business_function_id: string; label: string };
type MatrixResponse = { business_functions: BusinessFunction[]; skills: MatrixSkill[] };
type SkillLevelMaster = { skill_level_id: string; label: string };
// D-720 (2026-09-06): 抽象度は固定3値からテナント別マスタへ移行 (§13-3)。
type AbstractionLevel = { code: string; label: string; sort_order: number };

const UNDETERMINABLE_LABELS: Record<'scale_not_assigned' | 'face_disabled', string> = {
  scale_not_assigned: '尺度未設定のため判定不能', face_disabled: '無効化されたスキルのため判定不能',
};

export function TalentCoveragePage() {
  const user = useAuthStore((s) => s.user);
  const [deselectedIds, setDeselectedIds] = useState<Set<string>>(new Set());
  const [mobileNodeId, setMobileNodeId] = useState('');

  const { data: nodesData, isLoading: nodesLoading, isError: nodesError } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'),
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const selectableNodes = useMemo(() => (
    user?.stores && user.stores.length > 0
      ? flatNodes.filter((n) => user.stores!.includes(n.node.node_id))
      : flatNodes
  ), [flatNodes, user?.stores]);

  const selectedNodeIds = useMemo(
    () => selectableNodes.map((n) => n.node.node_id).filter((id) => !deselectedIds.has(id)),
    [selectableNodes, deselectedIds],
  );
  const toggleNode = (nodeId: string) => setDeselectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });

  const coverageQueries = useQueries({
    queries: selectedNodeIds.map((nodeId) => ({
      queryKey: ['competency-coverage', nodeId],
      queryFn: () => apiFetch<CoverageResponse>(`/competency/coverage?node_id=${encodeURIComponent(nodeId)}`),
    })),
  });
  const coverageByNode = new Map<string, FaceCoverageRow[]>();
  const coverageGroupsByNode = new Map<string, CoverageGroupRow[]>();
  selectedNodeIds.forEach((nodeId, i) => {
    const q = coverageQueries[i];
    if (q?.data) { coverageByNode.set(nodeId, q.data.faces); coverageGroupsByNode.set(nodeId, q.data.groups); }
  });
  const anyCoverageLoading = selectedNodeIds.length > 0 && coverageQueries.some((q) => q.isLoading);
  const { data: roleProfiles = [] } = useQuery<RoleProfileMaster[]>({ queryKey: ['role-profiles'], queryFn: () => apiFetch('/masters/role-profiles') });
  const roleProfileLabel = (id: string) => roleProfiles.find((rp) => rp.role_profile_id === id)?.label ?? id;

  // 面はテナント全体で共通 (D-687) のため、ラベル解決は選択中ノードのうち1件だけ /competency/matrix を叩けば足りる。
  const matrixNodeId = selectedNodeIds[0] ?? selectableNodes[0]?.node.node_id ?? '';
  const { data: matrixData } = useQuery<MatrixResponse>({
    queryKey: ['competency-matrix-labels', matrixNodeId],
    queryFn: () => apiFetch<MatrixResponse>(`/competency/matrix?node_id=${encodeURIComponent(matrixNodeId)}`),
    enabled: !!matrixNodeId,
  });
  const { data: skillLevels = [] } = useQuery<SkillLevelMaster[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });
  const levelLabel = (id: string | null) => (id ? skillLevels.find((l) => l.skill_level_id === id)?.label ?? id : '—');
  const { data: abstractionLevelsRaw = [] } = useQuery<AbstractionLevel[]>({ queryKey: ['abstraction-levels'], queryFn: () => apiFetch('/masters/abstraction-levels') });
  const abstractionLevels = [...abstractionLevelsRaw].sort((a, b) => a.sort_order - b.sort_order);
  const abstractionLevelLabelByCode = new Map(abstractionLevels.map((a) => [a.code, a.label]));
  const abstractionLevelOrderByCode = new Map(abstractionLevels.map((a, i) => [a.code, i]));

  const bfLabelById = new Map((matrixData?.business_functions ?? []).map((bf) => [bf.business_function_id, bf.label]));
  const skillById = new Map((matrixData?.skills ?? []).map((s) => [s.skill_id, s]));
  const faceLabel = (faceId: string) => skillById.get(faceId)?.label ?? faceId;
  const targetLabel = (g: CoverageGroupRow) => (g.target_entity_type === 'business_function' ? bfLabelById.get(g.target_id) : skillById.get(g.target_id)?.label) ?? g.target_id;

  // 列 (面) は選択中の全ノードの和集合から作る (通常は同一集合になるはずだが、防御的に union で組む)。
  const allFaceIds = [...new Set([...coverageByNode.values()].flatMap((faces) => faces.map((f) => f.face_id)))];
  const faceOrder = (faceId: string) => {
    const s = skillById.get(faceId);
    const bfLabel = s?.business_function_id ? bfLabelById.get(s.business_function_id) ?? '' : '';
    const levelOrder = s?.abstraction_level ? abstractionLevelOrderByCode.get(s.abstraction_level) ?? 99 : 99;
    return [bfLabel, levelOrder, s?.label ?? faceId] as const;
  };
  const sortedFaceIds = [...allFaceIds].sort((a, b) => {
    const oa = faceOrder(a); const ob = faceOrder(b);
    if (oa[0] !== ob[0]) return oa[0].localeCompare(ob[0], 'ja');
    if (oa[1] !== ob[1]) return oa[1] - ob[1];
    return oa[2].localeCompare(ob[2], 'ja');
  });

  const renderCell = (row: FaceCoverageRow | undefined) => {
    if (!row) return <span className="text-xs text-text-sub">—</span>;
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-sm font-semibold text-text-body whitespace-nowrap">{row.confirmed_count}名</span>
        {row.unconfirmed_count > 0 && (
          <span className="text-[11px] text-text-sub whitespace-nowrap">(未確認 {row.unconfirmed_count}名)</span>
        )}
        {row.undeterminable && (
          <span className="text-[11px] text-alert-text whitespace-nowrap">{UNDETERMINABLE_LABELS[row.undeterminable]}</span>
        )}
      </div>
    );
  };

  const nodeName = (nodeId: string) => selectableNodes.find((n) => n.node.node_id === nodeId)?.node.name ?? nodeId;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-page-title text-text-main">充足状況ビュー</h1>
          <p className="text-sm text-text-sub mt-1">
            店舗ごとの面 (業務×抽象度) の充足人数を確認します。処遇金額は表示しません。
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* v0.5 人材育成ダッシュボード: 旧 /talent/certifications・/talent/development-plans は
              廃止されタブ統合された (docs/design/v05-talent-development-dashboard-detail.md §0-2)。
              育成計画の登録はロースタータブの各メンバー詳細ドロワーへ移ったため、選択中の店舗を
              引き継いでロースタータブへ遷移させる。 */}
          <Link to="/talent/dashboard?tab=certifications"
            className="inline-flex items-center gap-1.5 text-xs text-chrome-base underline underline-offset-2 min-h-[44px] px-1">
            <BadgeCheck size={14} aria-hidden />認定の確定・インポートへ
          </Link>
          <Link
            to={matrixNodeId ? `/talent/dashboard?tab=roster&node_id=${encodeURIComponent(matrixNodeId)}` : '/talent/dashboard?tab=roster'}
            className="inline-flex items-center gap-1.5 text-xs text-chrome-base underline underline-offset-2 min-h-[44px] px-1">
            <GraduationCap size={14} aria-hidden />育成計画の登録へ (ロースターから)
          </Link>
        </div>
      </div>

      {nodesLoading ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : nodesError ? (
        <p role="alert" className="text-sm text-alert-text">組織情報の取得に失敗しました。</p>
      ) : selectableNodes.length === 0 ? (
        <EmptyState message="店舗が見つかりません" description="組織階層が設定されていないか、閲覧できる店舗がありません。" />
      ) : (
        <>
          <div>
            <div className="text-xs text-text-sub mb-1.5">対象店舗</div>
            <div className="flex flex-wrap gap-2">
              {selectableNodes.map(({ node }) => (
                <label key={node.node_id}
                  className="inline-flex items-center gap-1.5 text-xs border border-surface-border rounded-full px-2.5 min-h-[32px] cursor-pointer has-[:checked]:border-chrome-base has-[:checked]:bg-chrome-base/5">
                  <input type="checkbox" className="w-4 h-4 accent-chrome-base" checked={!deselectedIds.has(node.node_id)}
                    onChange={() => toggleNode(node.node_id)} aria-label={`${node.name} を対象に含める`} />
                  {node.name}
                </label>
              ))}
            </div>
          </div>

          {anyCoverageLoading && <p className="text-xs text-text-sub" aria-live="polite">充足状況を読み込み中…</p>}

          {selectedNodeIds.length === 0 ? (
            <p className="text-sm text-text-sub">上の「対象店舗」から少なくとも1店舗を選択してください。</p>
          ) : sortedFaceIds.length === 0 && !anyCoverageLoading ? (
            <EmptyState message="面 (業務×抽象度) がまだ登録されていません"
              description="「スキル・人材像」画面で業務・スキル・人材像を先に登録してください。" />
          ) : (
            <>
              {/* ≥768px: 店舗×面のグリッド (D-647) */}
              <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
                <div className="overflow-x-auto">
                  <table className="border-collapse text-sm">
                    <thead>
                      <tr>
                        <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 160 }}>
                          店舗
                        </th>
                        {sortedFaceIds.map((faceId) => {
                          const s = skillById.get(faceId);
                          const bfLabel = s?.business_function_id ? bfLabelById.get(s.business_function_id) : null;
                          return (
                            <th key={faceId} scope="col" className="relative border-b border-surface-border px-2 py-2 text-center text-xs text-text-sub" style={{ minWidth: 150 }}>
                              <div className="whitespace-nowrap">{faceLabel(faceId)}</div>
                              {bfLabel && s?.abstraction_level && (
                                <div className="text-[10px] text-text-sub whitespace-nowrap mt-0.5">
                                  {bfLabel} / {abstractionLevelLabelByCode.get(s.abstraction_level) ?? s.abstraction_level}
                                </div>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedNodeIds.map((nodeId) => {
                        const faces = coverageByNode.get(nodeId);
                        const byFace = new Map((faces ?? []).map((f) => [f.face_id, f]));
                        return (
                          <tr key={nodeId} className="odd:bg-white even:bg-surface-stripe">
                            <th scope="row" className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-2 text-left align-middle font-semibold text-text-body break-words">
                              {nodeName(nodeId)}
                            </th>
                            {sortedFaceIds.map((faceId) => (
                              <td key={faceId} className="px-2 py-2 text-center align-middle">
                                {renderCell(byFace.get(faceId))}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* <768px: 店舗を選択してドリルダウン (D-647) */}
              <div className="md:hidden space-y-2">
                <div className="max-w-xs">
                  <label className="block text-xs text-text-sub mb-1" htmlFor="coverage-node-mobile">店舗で絞り込み</label>
                  <Select value={mobileNodeId || '__none__'} onValueChange={(v) => setMobileNodeId(v === '__none__' ? '' : v)}>
                    <SelectTrigger id="coverage-node-mobile" aria-label="店舗で絞り込み"><SelectValue placeholder="選択してください" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">選択してください</SelectItem>
                      {selectedNodeIds.map((nodeId) => <SelectItem key={nodeId} value={nodeId}>{nodeName(nodeId)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!mobileNodeId ? (
                  <p className="text-sm text-text-sub">上の「店舗で絞り込み」からまず店舗を選択してください。</p>
                ) : (
                  (() => {
                    const faces = coverageByNode.get(mobileNodeId) ?? [];
                    const byFace = new Map(faces.map((f) => [f.face_id, f]));
                    if (sortedFaceIds.length === 0) return <EmptyState message="この店舗の面データがありません" />;
                    return (
                      <ul className="space-y-2">
                        {sortedFaceIds.map((faceId) => {
                          const s = skillById.get(faceId);
                          const bfLabel = s?.business_function_id ? bfLabelById.get(s.business_function_id) : null;
                          const row = byFace.get(faceId);
                          return (
                            <li key={faceId} className="border border-surface-border rounded-lg bg-white p-3 flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-text-body break-words">{faceLabel(faceId)}</div>
                                {bfLabel && s?.abstraction_level && (
                                  <div className="text-[11px] text-text-sub">{bfLabel} / {abstractionLevelLabelByCode.get(s.abstraction_level) ?? s.abstraction_level}</div>
                                )}
                                <div className="text-[11px] text-text-sub">必要習熟度: {levelLabel(row?.required_scale_level_id ?? null)}</div>
                              </div>
                              <div className="shrink-0">{renderCell(row)}</div>
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()
                )}
              </div>
            </>
          )}

          {/* v0.5 ② (D-787/§3-1-6): 親ノード要求の充足。給与算入が既定OFFのテナントでは
              ここが唯一の確認手段になる (§5-6)。 */}
          {selectedNodeIds.length > 0 && [...coverageGroupsByNode.values()].some((gs) => gs.length > 0) && (
            <div className="space-y-2">
              <h2 className="text-sm font-bold text-text-body">親ノード要求の充足</h2>
              <p className="text-xs text-text-sub">
                業務・スキルの分類 (くくり) を必要スキルに指定した人材像について、店舗ごとの充足人数を確認できます。
              </p>
              {selectedNodeIds.map((nodeId) => {
                const groups = coverageGroupsByNode.get(nodeId) ?? [];
                if (groups.length === 0) return null;
                return (
                  <div key={nodeId} className="border border-surface-border rounded-lg bg-white p-3">
                    <div className="text-sm font-semibold text-text-body mb-2 break-words">{nodeName(nodeId)}</div>
                    <ul className="space-y-2">
                      {groups.map((g, i) => (
                        <li key={i} className="flex items-center justify-between gap-2 flex-wrap border-t border-surface-line pt-2 first:border-t-0 first:pt-0">
                          <div className="min-w-0">
                            <div className="text-sm text-text-body break-words">
                              {roleProfileLabel(g.role_profile_id)} <span className="text-text-sub">/</span> {targetLabel(g)}
                            </div>
                            <div className="text-[11px] text-text-sub">{AGGREGATION_LABELS[g.aggregation]}</div>
                            {g.undeterminable && (
                              <div className="text-[11px] text-alert-text">{UNDETERMINABLE_LABELS[g.undeterminable]}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {g.fully_covered_by_explicit && (
                              <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">個別指定で充足</span>
                            )}
                            <span className="text-sm font-semibold text-text-body whitespace-nowrap">{g.satisfied_count} / {g.subject_count}名</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
